import { signupStep1Schema } from "#/modules/generic.signup/schema.signup.step-1";
import { signupStep2Schema } from "#/modules/generic.signup/schema.signup.step-2";
import { getSignupSession } from "#/modules/generic.signup/session.signup.server";
import { getSchoolDataByBrin, validateBrin } from "#/modules/school/api.school.server";
import { toResult } from "#/modules/util.auth/api-errors";
import { getUserInfo, userExists, validatePassword } from "#/modules/util.auth/api.auth.server";
import { getAuthSession } from "#/modules/util.auth/session.auth.server";
import { translateApiError } from "#/modules/util.errors/translate";
import { client } from "#/modules/util.fetch-client/fetch-client.server";
import { createServerFn } from "@tanstack/react-start";
import { Result } from "better-result";
import { z } from "zod/v4";

type SignupSessionData = {
  name: string;
  surname: string;
  email: string;
  /** Whether a password was already captured in step 1 — the plaintext value is never sent to the client. */
  hasPassword: boolean;
  brin?: string;
  validSteps: number[];
};

// ---------------------------------------------------------------------------
// Step 1 — name / surname / email / password
// ---------------------------------------------------------------------------

/** Returns which signup steps have been completed (for client-side step guards). */
export const getSignupValidStepsFn = createServerFn({ method: "GET" }).handler(async () => {
  const session = await getSignupSession();
  return session.data.validSteps ?? [];
});

/** Look up schools by BRIN for the step-2 live preview (no session write). */
export const lookupBrinFn = createServerFn({ method: "GET" })
  .validator(z.object({ brin: z.string() }))
  .handler(async ({ data }) => {
    if (!data.brin || data.brin.length < 4) return { schools: null };
    const result = await getSchoolDataByBrin(data.brin);
    return { schools: result.unwrapOr(undefined) ?? null };
  });

/**
 * Returns the saved step-1 data for pre-filling the form. The plaintext password is never
 * sent to the client — only whether one was already captured (`hasPassword`), so a revisit
 * of a stale session makes the user retype it rather than prefilling it.
 */
export const getSignupStep1DataFn = createServerFn({ method: "GET" }).handler(async () => {
  const session = await getSignupSession();
  return {
    name: session.data.name ?? "",
    surname: session.data.surname ?? "",
    email: session.data.email ?? "",
    hasPassword: Boolean(session.data.password),
  };
});

/**
 * Validates step-1 fields (including async API checks for uniqueness + password strength),
 * persists to the signup session on success; returns { formError } on failure.
 */
export const submitSignupStep1Fn = createServerFn({ method: "POST" })
  .validator(signupStep1Schema)
  .handler(async ({ data }): Promise<{ formError: string } | undefined> => {
    // Async checks: user-exists and password-valid
    const [userExistsResult, passwordResult] = await Promise.all([
      userExists(data.email),
      validatePassword(data.password),
    ]);

    const invalid = userExistsResult ?? passwordResult;
    if (invalid) {
      return invalid;
    }

    const session = await getSignupSession();
    const validSteps = session.data.validSteps ?? [];
    await session.update({
      name: data.name,
      surname: data.surname,
      email: data.email,
      password: data.password,
      validSteps: [...new Set([...validSteps, 1])],
    });

    return undefined;
  });

// ---------------------------------------------------------------------------
// Step 2 — BRIN school lookup
// ---------------------------------------------------------------------------

/** Returns the saved step-2 data (brin) for pre-filling the form. */
export const getSignupStep2DataFn = createServerFn({ method: "GET" }).handler(async () => {
  const session = await getSignupSession();
  const brin = session.data.brin ?? "";
  const schools = brin ? (await getSchoolDataByBrin(brin)).unwrapOr(undefined) : undefined;
  return { brin, schools: schools ?? null };
});

/**
 * Validates the BRIN (including async API check), normalises it when a single school is found,
 * persists to the signup session; returns { formError } on failure.
 */
export const submitSignupStep2Fn = createServerFn({ method: "POST" })
  .validator(signupStep2Schema)
  .handler(async ({ data }): Promise<{ formError: string } | undefined> => {
    const brinResult = await validateBrin(data.brin);
    if (brinResult) {
      return { formError: brinResult.formError };
    }

    // If a single school is found, swap to the canonical BRIN from the response.
    const schools = (await getSchoolDataByBrin(data.brin)).unwrapOr(undefined);
    const resolvedBrin = schools?.length === 1 ? (schools[0]!.brin ?? data.brin) : data.brin;

    const session = await getSignupSession();
    const validSteps = session.data.validSteps ?? [];
    await session.update({
      brin: resolvedBrin,
      validSteps: [...new Set([...validSteps, 2])],
    });

    return undefined;
  });

// ---------------------------------------------------------------------------
// Step 3 — register (loader-only, no form)
// ---------------------------------------------------------------------------

/**
 * Reads the signup session data for the confirmation step. The plaintext password is not
 * included in the response — the account-creation submit (`submitSignupStep3Fn`) reads it
 * server-side directly from the session.
 */
export const getSignupStep3DataFn = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ formError: string } | { data: SignupSessionData }> => {
    const session = await getSignupSession();
    const { name, surname, email, password, brin, validSteps } = session.data;

    if (!name || !surname || !email || !password) {
      return { formError: m["signup.session_incomplete"]() };
    }

    return {
      data: { name, surname, email, hasPassword: true, brin, validSteps: validSteps ?? [] },
    };
  },
);

/**
 * POST /scholen/registratie — registers the school + user and logs the user in. On
 * success (undefined) the caller navigates to the typed success route itself. Returns
 * { formError } on API failure.
 */
export const submitSignupStep3Fn = createServerFn({ method: "POST" })
  .validator(z.object({}))
  .handler(async (): Promise<{ formError: string } | undefined> => {
    const session = await getSignupSession();
    const { name, surname, email, password, brin } = session.data;

    if (!name || !surname || !email || !password) {
      return { formError: m["signup.session_incomplete"]() };
    }

    const registerResult = toResult(
      await client.POST("/scholen/registratie", {
        body: { firstName: name, lastName: surname, email, password, brin },
      }),
    );

    if (Result.isError(registerResult)) {
      return { formError: translateApiError(registerResult.error) };
    }

    // Log the user in automatically after registration (failure deliberately ignored).
    const loginResult = toResult(
      await client.POST("/auth/login", {
        body: { email, password },
      }),
    );

    if (Result.isOk(loginResult) && loginResult.value) {
      const userResult = await getUserInfo(loginResult.value.accessToken ?? "");
      if (Result.isOk(userResult) && userResult.value) {
        const authSession = await getAuthSession();
        await authSession.update({ tokens: loginResult.value, user: userResult.value });
      }
    }

    // Clear the signup session.
    await session.clear();

    // Caller navigates to the typed success route itself.
    return undefined;
  });
